import { useInfiniteQuery, useMutation, useQuery } from 'react-query'
import { api } from '../../utils/api'
import { clearToken } from '../../utils/helper'
import { EditUserFormValues, UserCoinTransaction, UserType } from './user-typed'
import { queryCache } from '../../index'
import { ContentType } from '../../utils/api/tools'

export const EDIT_USER_FORM_NAME = 'EditUserFormName'

export const USER_URL = '/app/user'
export const GET_USER_URL = `${USER_URL}/getUser`
export const EDIT_USER_URL = `${USER_URL}/edit`
export const GET_USER_COIN_URL = `${USER_URL}/getUserCoin`
export const GET_USER_EXPIRED_COIN_URL = `${USER_URL}/getUserEXPCoin`
export const GET_USER_COIN_TRANSACTION_URL = `${USER_URL}/getCoinTransaction`

export const useGetUser = () => {
  return useQuery<UserType | undefined, Error>(
    GET_USER_URL,
    () => {
      return api.belive.get<UserType>(GET_USER_URL)
    },
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      onError: () => {
        clearToken()
      },
      retry: 0,
    },
  )
}

export const useGetUserCoin = () => {
  return useQuery(GET_USER_COIN_URL, async () => {
    return await api.belive.get<number>(GET_USER_COIN_URL)
  })
}

export const useGetUserExpiredCoin = () => {
  return useQuery(GET_USER_EXPIRED_COIN_URL, async () => {
    return await api.belive.get<string>(GET_USER_EXPIRED_COIN_URL)
  })
}

export const useFetchUserCoinTransaction = () => {
  return useInfiniteQuery(
    [GET_USER_COIN_TRANSACTION_URL, { size: 10 }],
    ({ pageParam = 1 }) =>
      api.belive.get<UserCoinTransaction>(GET_USER_COIN_TRANSACTION_URL, {
        page: pageParam,
        size: 10,
      }),
    {
      getNextPageParam: (lastGroup: UserCoinTransaction) => {
        const { totalPages, number } = lastGroup
        const curPage = number + 1
        const nextPage = number + 2
        return curPage < totalPages ? nextPage : null
      },
    },
  )
}

export const setUser = (user: UserType) => {
  queryCache.setQueryData(GET_USER_URL, user)
}

export const logout = () => {
  queryCache.setQueryData(GET_USER_URL, undefined)
  clearToken()
  queryCache.removeQueries()
}

export const useEditUser = (userId?: number) => {
  return useMutation(
    [EDIT_USER_URL, { userId }],
    async (form: EditUserFormValues) => {
      const params = {
        id: userId,
        address: form.address,
        phoneNo: form.phoneNumber,
      }
      return (
        userId !== undefined &&
        (await api.belive.post(EDIT_USER_URL, params, {
          contentType: ContentType.JSON,
        }))
      )
    },
    {
      onSuccess: () => {
        queryCache.invalidateQueries({
          predicate: (query) => {
            const { queryHash } = query
            if (queryHash.includes(GET_USER_URL)) return true
            else return false
          },
        })
      },
    },
  )
}
